import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { Calendar } from './Calendar';
import styles from './DateRangePickerWidget.module.scss';

type DateRangePickerPayload = {
  min_date: string;
  max_date: string;
  default_from?: string;
  default_to?: string;
  submitted_value?: {
    from: string;
    to: string;
    nights: number;
  };
};

type Edge = 'from' | 'to';

const formatTileDate = (iso: string) => {
  const date = iso ? parseISO(iso) : null;
  return date && isValid(date) ? format(date, 'EEE, MMM d') : null;
};

export function DateRangePickerWidget({
  payload,
  onSubmit,
  labels = { from: 'From', to: 'To' },
}: {
  payload: DateRangePickerPayload;
  onSubmit: (value: { from: string; to: string; nights: number }) => void;
  labels?: { from: string; to: string };
}) {
  const [from, setFrom] = useState(payload.submitted_value?.from || payload.default_from || payload.min_date || '');
  const [to, setTo] = useState(payload.submitted_value?.to || payload.default_to || payload.max_date || '');
  // Which end of the range the next click on the calendar sets.
  const [editing, setEditing] = useState<Edge>('from');
  const nights = useMemo(
    () => (from && to ? Math.max(1, differenceInCalendarDays(parseISO(to), parseISO(from))) : 0),
    [from, to]
  );

  // Click a start, then an end. A click on or before the start while
  // picking the end starts the range over from that day instead.
  const pick = (iso: string) => {
    if (editing === 'to' && from && iso > from) {
      setTo(iso);
      setEditing('from');
      return;
    }
    setFrom(iso);
    if (editing === 'to' || (to && to <= iso)) setTo('');
    setEditing('to');
  };

  const canSubmit = Boolean(from && to);

  const renderTile = (edge: Edge, value: string) => {
    const display = formatTileDate(value);
    return (
      <button
        type="button"
        className={`${styles.tile} ${editing === edge ? styles.tileActive : ''}`}
        aria-pressed={editing === edge}
        onClick={() => setEditing(edge)}
      >
        <span className={styles.tileLabel}>{labels[edge]}</span>
        <span className={display ? styles.tileValue : styles.tilePlaceholder}>{display ?? 'Add date'}</span>
      </button>
    );
  };

  return (
    <section className={styles.root} aria-label="Choose dates">
      <h3 className={styles.title}>Choose dates</h3>

      <div className={styles.row}>
        {renderTile('from', from)}
        {renderTile('to', to)}
      </div>

      <Calendar
        start={from}
        end={to}
        selectingEnd={editing === 'to'}
        min={payload.min_date}
        max={payload.max_date}
        onSelect={pick}
        maxMonths={2}
      />

      <div className={styles.footer}>
        <p className={styles.nights}>
          {canSubmit ? `${nights} night${nights === 1 ? '' : 's'}` : 'Pick an end date'}
        </p>
        <button
          type="button"
          className={styles.submitButton}
          aria-disabled={!canSubmit}
          onClick={() => canSubmit && onSubmit({ from, to, nights })}
        >
          Submit
        </button>
      </div>
    </section>
  );
}
